package com.milta.feature.importjobs.data.remote.dto

import com.milta.feature.imports.data.remote.dto.ImportPdfResultDto
import com.milta.feature.imports.data.remote.dto.toDomain
import com.milta.feature.importjobs.domain.entity.ImportJob
import com.milta.feature.importjobs.domain.entity.ImportJobFlow
import com.milta.feature.importjobs.domain.entity.ImportJobResult
import com.milta.feature.importjobs.domain.entity.ImportJobStage
import com.milta.feature.importjobs.domain.entity.ImportJobStatus
import com.milta.feature.ocrscan.data.remote.dto.OcrScanResultDto
import com.milta.feature.ocrscan.data.remote.dto.toDomain
import com.milta.feature.spreadsheetimport.data.remote.dto.SpreadsheetImportResultDto
import com.milta.feature.spreadsheetimport.data.remote.dto.toDomain
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlin.time.Instant

/**
 * Mirrors the backend `ImportJobDto` (`/api/v1/import-jobs`). [result] stays a raw [JsonObject]
 * here: its shape depends on [flow] and is exactly the DTO of the former synchronous endpoint
 * (ImportPdfResultDto, SpreadsheetImportResultDto or OcrScanResultDto), decoded in [toDomain].
 */
@Serializable
data class ImportJobDto(
    val id: String,
    val flow: ImportJobFlow,
    val status: ImportJobStatus,
    val errorCode: String? = null,
    val attemptCount: Int = 0,
    // Retour 27/10 — no attempt limit any more (the AI credit controls the cost): a
    // `maxAttemptsReached` still sent by an older server is ignored (ignoreUnknownKeys).
    val fileName: String = "",
    val createdAt: String = "",
    val updatedAt: String? = null,
    /** Existing route the import was launched from; null = launched while creating a new route. */
    val routeId: String? = null,
    /** `yyyy-MM-dd` of [routeId]'s route; null when [routeId] is null. */
    val routeDate: String? = null,
    /** The document was already read before a failure: a retry only re-checks the addresses. */
    val extractionCompleted: Boolean = false,
    val result: JsonObject? = null,
    // Retour 26/10 — real progress, all nullable (absent with an older server).
    /** "Reading" | "Geocoding" | null — a String, decoded leniently in [toDomain]. */
    val stage: String? = null,
    /** ISO 8601. */
    val stageStartedAt: String? = null,
    val readingDurationSeconds: Int? = null,
    val deliveryCount: Int? = null,
    val geocodedCount: Int? = null,
    val geocodeTotal: Int? = null,
)

// Same settings as the HttpClient's ContentNegotiation (MiltaApiClient.kt).
private val resultJson = Json {
    ignoreUnknownKeys = true
    isLenient = true
}

fun ImportJobDto.toDomain() = ImportJob(
    id = id,
    flow = flow,
    status = status,
    errorCode = errorCode,
    attemptCount = attemptCount,
    fileName = fileName,
    createdAt = createdAt,
    updatedAt = updatedAt,
    routeId = routeId,
    routeDate = routeDate,
    extractionCompleted = extractionCompleted,
    result = if (status.hasResult) result?.toDomainResult(flow) else null,
    stage = ImportJobStage.fromApi(stage),
    stageStartedAtEpochMillis = stageStartedAt?.let { runCatching { Instant.parse(it).toEpochMilliseconds() }.getOrNull() },
    readingDurationSeconds = readingDurationSeconds,
    deliveryCount = deliveryCount,
    geocodedCount = geocodedCount,
    geocodeTotal = geocodeTotal,
)

private fun JsonObject.toDomainResult(flow: ImportJobFlow): ImportJobResult = when (flow) {
    ImportJobFlow.PDF -> ImportJobResult.Pdf(
        resultJson.decodeFromJsonElement(ImportPdfResultDto.serializer(), this).toDomain(),
    )
    ImportJobFlow.SPREADSHEET -> ImportJobResult.Spreadsheet(
        resultJson.decodeFromJsonElement(SpreadsheetImportResultDto.serializer(), this).toDomain(),
    )
    ImportJobFlow.OCR_DELIVERY_LIST -> ImportJobResult.OcrDeliveryList(
        resultJson.decodeFromJsonElement(OcrScanResultDto.serializer(), this).toDomain(),
    )
}
